// AuthModal.js — connexion / inscription (e-mail + invité + Google/Facebook si activés).
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiX, FiMail, FiUser } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { socialAuth, anySocialEnabled } from '../config/socialAuth';
import { loadGoogleToken, loadFacebookToken } from '../services/oauthClients';
import { subscribeNewsletter } from '../services/api';
import { locale } from '../config/i18n';

// Logo Google officiel (« G » 4 couleurs) + logo Facebook — pour des boutons social aux standards de marque.
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#fff" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
);

const NL_THEMES = [
  { id: 'jeux', label: '🎮 Jeux' },
  { id: 'anime', label: '⚔️ Anime & Manga' },
  { id: 'science', label: '🔭 Science & Sport' },
  { id: 'revenus', label: '💰 Revenus & bons plans' },
];

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,.6);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 1rem; overflow-y: auto; -webkit-overflow-scrolling: touch;`;
const Box = styled.div`
  width: min(400px, 100%); margin: auto; background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.borderHover}; border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.card};
  max-height: calc(100vh - 2rem); overflow-y: auto;`;
const Head = styled.div`
  position: sticky; top: 0; z-index: 1;
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.2rem; background: ${theme.colors.gradient}; color: #fff;
  h3 { font-family: ${theme.fonts.heading}; font-size: 1.05rem; }
  button { background: none; border: none; color: #fff; cursor: pointer; font-size: 1.2rem; display: flex; }`;
const Body = styled.div`padding: 1.2rem; display: flex; flex-direction: column; gap: .7rem;`;
const Field = styled.label`
  display: flex; align-items: center; gap: .5rem; background: ${theme.colors.bgPrimary};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: .55rem .8rem;
  svg { color: ${theme.colors.textMuted}; flex: none; }
  input { flex: 1; background: none; border: none; color: ${theme.colors.textPrimary}; font-size: .9rem; outline: none; }`;
const Primary = styled.button`
  border: none; cursor: pointer; background: ${theme.colors.gradient}; color: #fff;
  padding: .65rem; border-radius: ${theme.radii.full}; font-weight: 700; font-size: .9rem;
  &:disabled { opacity: .6; cursor: default; }`;
const Ghost = styled.button`
  border: 1px solid ${theme.colors.border}; cursor: pointer; background: none; color: ${theme.colors.textSecondary};
  padding: .55rem; border-radius: ${theme.radii.full}; font-size: .82rem;`;
const Social = styled.button`
  display: flex; align-items: center; justify-content: center; gap: .6rem;
  cursor: pointer; padding: .68rem .9rem; border-radius: ${theme.radii.full}; font-weight: 600; font-size: .9rem;
  border: 1px solid transparent; transition: filter .15s, background .15s, transform .05s;
  &:active { transform: translateY(1px); }
  &:disabled { opacity: .55; cursor: default; }
  & svg { flex: none; }
  &.g { background: #fff; color: #3c4043; border-color: #dadce0; box-shadow: 0 1px 3px rgba(0,0,0,.28); }
  &.g:hover:not(:disabled) { background: #f6f7f8; box-shadow: 0 2px 6px rgba(0,0,0,.32); }
  &.f { background: #1877F2; color: #fff; }
  &.f:hover:not(:disabled) { filter: brightness(1.06); }`;
const Sep = styled.div`display: flex; align-items: center; gap: .6rem; color: ${theme.colors.textMuted}; font-size: .72rem; margin: .2rem 0;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: ${theme.colors.border}; }`;
const Err = styled.p`color: #f87171; font-size: .8rem; min-height: 1em;`;
const Toggle = styled.p`text-align: center; font-size: .8rem; color: ${theme.colors.textMuted};
  button { background: none; border: none; color: ${theme.colors.primaryLight}; cursor: pointer; font-weight: 600; }`;

export default function AuthModal({ onClose, initialMode }) {
  const { auth, refreshAccount, isGuest } = useAuth();
  const [mode, setMode] = useState(initialMode === 'register' ? 'register' : 'login'); // login | register
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [pseudo, setPseudo] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [nlOptin, setNlOptin] = useState(true);
  const [nlThemes, setNlThemes] = useState(['jeux']);
  const [forgot, setForgot] = useState(false);

  const toggleTheme = (id) => setNlThemes((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]);

  const done = async () => { await refreshAccount(); setBusy(false); onClose(); };
  const fail = (m) => { setErr(m); setBusy(false); };

  const submitEmail = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      if (!email || !pass) return fail('E-mail et mot de passe requis.');
      // INVITÉ qui crée un compte → on LIE l'e-mail au compte invité courant : l'XP est CONSERVÉE.
      if (mode === 'register' && isGuest) {
        const r = await auth.linkEmail(email.trim(), pass);
        if (r.ok) {
          if (pseudo) { try { await auth.updateAccount({ display_name: pseudo }); } catch { /* */ } }
          if (nlOptin && nlThemes.length) { try { await subscribeNewsletter(email.trim(), locale, nlThemes); } catch { /* */ } }
          return done();
        }
        if (r.error === 'taken') return fail('Cet e-mail est déjà lié à un autre compte. Connecte-toi plutôt (ta progression invité reste sur cet appareil).');
        // sinon on retombe sur la création classique ci-dessous
      }
      await auth.authEmail(email.trim(), pass, mode === 'register', mode === 'register' ? (pseudo || undefined) : undefined);
      // Inscription newsletter (opt-in) — best-effort, ne bloque pas la connexion
      if (mode === 'register' && nlOptin && nlThemes.length) {
        try { await subscribeNewsletter(email.trim(), locale, nlThemes); } catch { /* ignore */ }
      }
      await done();
    } catch { fail(mode === 'register' ? 'Inscription impossible (e-mail déjà pris ?).' : 'Identifiants invalides.'); }
  };
  const asGuest = async () => {
    setErr(''); setBusy(true);
    try { await auth.authGuest(pseudo || undefined); await done(); } catch { fail('Connexion invité impossible.'); }
  };
  const withGoogle = async () => {
    setErr(''); setBusy(true);
    try {
      const { token, name } = await loadGoogleToken();
      // INVITÉ → on LIE Google au compte courant (XP conservée) ; si déjà pris, on bascule sur ce compte.
      if (isGuest) { const r = await auth.linkGoogle(token); if (r.ok) return done(); if (r.error === 'taken') return fail('Ce compte Google est déjà utilisé — reconnecte-toi avec (ta progression invité reste sur cet appareil).'); }
      await auth.authGoogle(token, name); await done();
    }
    catch { fail('Connexion Google indisponible.'); }
  };
  const withFacebook = async () => {
    setErr(''); setBusy(true);
    try {
      const { token, name } = await loadFacebookToken();
      if (isGuest) { const r = await auth.linkFacebook(token); if (r.ok) return done(); if (r.error === 'taken') return fail('Ce compte Facebook est déjà utilisé — reconnecte-toi avec (ta progression invité reste sur cet appareil).'); }
      await auth.authFacebook(token, name); await done();
    }
    catch { fail('Connexion Facebook indisponible.'); }
  };

  // Rendu via PORTAL vers <body> : la Toolbar a un backdrop-filter/animation qui crée un bloc
  // conteneur pour les position:fixed → sans portal, la popup serait confinée à la hauteur de la barre.
  return createPortal(
    <Overlay onClick={onClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <Head>
          <h3>{mode === 'login' ? 'Se connecter' : 'Créer un compte'}</h3>
          <button onClick={onClose} aria-label="Fermer"><FiX /></button>
        </Head>
        <Body>
          {isGuest && (
            <div style={{ background: 'rgba(124,58,237,.12)', border: `1px solid ${theme.colors.borderHover}`, borderRadius: 8, padding: '.5rem .7rem', fontSize: '.78rem', color: theme.colors.textSecondary }}>
              ✨ Tu es en <b>invité</b>. Crée ton compte (Google, Facebook ou e-mail) : <b>ta progression et ton XP seront conservés</b>.
            </div>
          )}
          {anySocialEnabled && (
            <>
              {socialAuth.google.enabled && <Social className="g" onClick={withGoogle} disabled={busy}><GoogleIcon />Continuer avec Google</Social>}
              {socialAuth.facebook.enabled && <Social className="f" onClick={withFacebook} disabled={busy}><FacebookIcon />Continuer avec Facebook</Social>}
              <Sep>ou</Sep>
            </>
          )}
          {socialAuth.emailEnabled && (
            <form onSubmit={submitEmail} style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
              {mode === 'register' && (
                <Field><FiUser /><input placeholder="Pseudo" value={pseudo} onChange={(e) => setPseudo(e.target.value)} maxLength={40} /></Field>
              )}
              <Field><FiMail /><input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
              <Field><FiUser /><input type="password" placeholder="Mot de passe" value={pass} onChange={(e) => setPass(e.target.value)} /></Field>
              {mode === 'register' && (
                <div style={{ background: theme.colors.bgPrimary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.md, padding: '.6rem .8rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.82rem', color: theme.colors.textSecondary, cursor: 'pointer' }}>
                    <input type="checkbox" checked={nlOptin} onChange={(e) => setNlOptin(e.target.checked)} />
                    Recevoir nos actus par email (thématiques ci-dessous)
                  </label>
                  {nlOptin && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', marginTop: '.5rem' }}>
                      {NL_THEMES.map((th) => (
                        <button key={th.id} type="button" onClick={() => toggleTheme(th.id)}
                          style={{ cursor: 'pointer', fontSize: '.74rem', padding: '.28rem .6rem', borderRadius: theme.radii.full,
                            border: `1px solid ${nlThemes.includes(th.id) ? 'transparent' : theme.colors.border}`,
                            background: nlThemes.includes(th.id) ? theme.colors.gradient : 'none',
                            color: nlThemes.includes(th.id) ? '#fff' : theme.colors.textMuted }}>
                          {th.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Primary type="submit" disabled={busy}>{mode === 'login' ? 'Connexion' : 'Créer mon compte'}</Primary>
              {mode === 'login' && (
                <p style={{ textAlign: 'center', fontSize: '.76rem', margin: '.1rem 0 0' }}>
                  <button type="button" onClick={() => setForgot((v) => !v)}
                    style={{ background: 'none', border: 'none', color: theme.colors.primaryLight, cursor: 'pointer' }}>
                    Mot de passe oublié ?
                  </button>
                </p>
              )}
              {mode === 'login' && forgot && (
                <div style={{ background: theme.colors.bgPrimary, border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radii.md, padding: '.6rem .8rem', fontSize: '.78rem', color: theme.colors.textSecondary, lineHeight: 1.5 }}>
                  Connecte-toi avec ton <b>e-mail</b> (pas ton pseudo) et ton mot de passe.<br />
                  Mot de passe perdu ? Écris à <b>{process.env.REACT_APP_SUPPORT_EMAIL || 'support@vertingo'}</b> avec l'e-mail de ton compte : on te le réinitialise.
                </div>
              )}
            </form>
          )}
          {socialAuth.guestEnabled && <Ghost onClick={asGuest} disabled={busy}>Continuer en invité</Ghost>}
          <Err>{err}</Err>
          <Toggle>
            {mode === 'login'
              ? <>Pas de compte ? <button onClick={() => { setMode('register'); setErr(''); }}>Créer un compte</button></>
              : <>Déjà membre ? <button onClick={() => { setMode('login'); setErr(''); }}>Se connecter</button></>}
          </Toggle>
        </Body>
      </Box>
    </Overlay>,
    document.body
  );
}
